import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  CBadge, CAlert, CButton, CButtonGroup, CCard, CCardBody, CCardHeader, CDropdown, CDropdownDivider,
  CDropdownItem, CDropdownMenu, CDropdownToggle, CFormCheck, CFormInput, CFormLabel, CSpinner,
  CTable, CTableBody, CTableDataCell, CTableHead, CTableHeaderCell, CTableRow, CTooltip,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import * as icons from '@coreui/icons'
import { normalizarAcciones, accionesInlineEnd, accionesOverflow, accionesParaFila } from './acciones'
import { fmtMoneda } from '../../utils/moneda'
import { api, getUser } from '../../api/client'
import { t } from '../../i18n'

// [RGM] 2026-10-10 (pedido Rafael: "mover las columnas en grilla, o con el
// selector de columnas — al refrescar la página se pierde la configuración"):
// persistencia de la personalización de la grilla (orden de columnas,
// visibilidad y fijadas) en localStorage — patrón ya usado en el proyecto
// (NotificacionesPanel/TroniaFab). Fiel al GX/WWP, que guardaba el GridState
// por USUARIO + grilla (UserKey + GridStateKey —
// wwbaseobjects/savefilteras.cs:270-288): la clave incluye usuario + ruta +
// título para no mezclar personalizaciones entre usuarios de la misma máquina
// ni entre pantallas distintas con el mismo título.
type GridPersonalizacion = {
  orden: string[]
  visibles: Record<string, boolean>
  fijadasL: string[]
  fijadasR: string[]
}

function claveGridPersonalizacion(title: string): string {
  const usuario = getUser()?.id ?? 'anon'
  const ruta = window.location.pathname
  return `wwgrid:${usuario}:${ruta}:${title}`
}

function leerGridPersonalizacion(title: string): GridPersonalizacion | null {
  try {
    const raw = localStorage.getItem(claveGridPersonalizacion(title))
    return raw ? (JSON.parse(raw) as GridPersonalizacion) : null
  } catch {
    return null
  }
}

export interface WwColumn {
  key: string
  title: string
  type?: 'text' | 'number' | 'date' | 'bool' | 'currency' | 'id'
  sortable?: boolean
  link?: boolean
  /** Render personalizado de la celda (recibe la fila completa) — badge/UI
   *  especial (ej. estado de ruta). Reemplaza al formateo por tipo. */
  render?: (row: any) => React.ReactNode
  /** [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
   *  valor PLANO de la celda para el export cuando el texto visible difiere
   *  del crudo (ej. Método 1 → 'Efectivo', Modalidad 1 → 'DIARIO'). Si no se
   *  define, se exporta el crudo (los type 'bool' salen Sí/No). */
  exportValue?: (row: any) => string | number | null
  /** [RGM] 2026-09-29 (pedido Rafael: "sticky las acciones de la fila"):
   *  columna fija desde el inicio (ej. 'right' para la columna Acciones). */
  sticky?: 'left' | 'right'
  /** Ancho de columna explícito (CSS width; también fija el mínimo). */
  width?: string | number
  /** Ancho mínimo en px si no se quiere el default por tipo. */
  minWidth?: number
}

export interface WwRowAction {
  id?: string
  label: string
  icon?: string
  /** inline-start (inicio de fila) | inline-end (final) | overflow (menú ⋮). */
  placement?: 'inline-start' | 'inline-end' | 'overflow'
  /** inserta la acción como celda propia DESPUÉS de la columna con esta key. */
  insertAfter?: string
  /** Acción destructiva: al final del ⋮, separada, en danger. */
  destructive?: boolean
  onClick: (row: any) => void
  /** Si se define, la acción solo se muestra cuando devuelve true para la fila. */
  mostrar?: (row: any) => boolean
}

export interface WwGridProps {
  title: string
  /** [RGM] 2026-09-29 (pedido Rafael): oculta la tarjeta de herramientas
   *  (acciones, búsqueda y filtros) — para grillas de sección en las que solo
   *  va el título propio de la sección y la tabla. */
  sinHerramientas?: boolean
  /** [RGM] 2026-09-29 (pedido Rafael: "el título va sobre la grilla, como las
   *  pantallas de edit"): muestra el título DENTRO de la cabecera de la
   *  tarjeta de la grilla (CCardHeader). Opt-in para no duplicar títulos. */
  tituloCabecera?: boolean
  columns: WwColumn[]
  rows: any[]
  loading?: boolean
  onAgregar?: () => void
  /** [RGM] 2026-10-02 (pedido Rafael — export .xlsx real): si la página
   *  provee este handler, recibe las filas filtradas+ordenadas actuales
   *  (todas, sin paginar) y hace su propia exportación (ej. Préstamos: 27
   *  columnas del WW GX). Sin handler, el grid exporta a .xlsx por el
   *  endpoint genérico /export/grid con el mismo formato aprobado. */
  onExportar?: (filasFiltradas: any[], filtrosGrilla: { label: string; valor: string }[]) => void
  onEditar?: (row: any) => void
  onEliminar?: (row: any) => void
  rowActions?: WwRowAction[]
  /** Ancho mínimo de la columna de acciones (default 110px — caben 2 iconos + ⋮). */
  accionesMinWidth?: number
  onRowLink?: (colKey: string, row: any) => void
  /** Al hacer clic en una FILA (grid) — selección de detalle (ej. Cierre Diario). */
  onRowClick?: (row: any) => void
  /** Notifica el total filtrado+ordenado (sin paginar) — contador del subtítulo de página */
  onTotalChange?: (total: number) => void
  /** Placeholder del filtro principal (búsqueda general con lupa). Por
      defecto "Filtro principal"; cada página pone la indicación de cómo
      buscar (ej. "Buscar por cliente (cédula, nombre, dirección o celular)..."). */
  busquedaPlaceholder?: string
  /**
   * [RGM] 2026-09-17 (pedido Rafael): filtros/botones externos de la página
   * (ej. <WwFilterBar/>) — se renderizan DENTRO de la tarjeta de la grilla,
   * debajo del cuadro de búsqueda.
   */
  filters?: React.ReactNode
}

type Vista = 'grid' | 'cards-small' | 'cards-info' | 'cards-big'

type FiltroCol =
  | { tipo: 'texto'; valores: string[] }
  | { tipo: 'rango'; desde: string; hasta: string }
  | { tipo: 'bool'; valor: boolean }

interface FiltroPanelProps {
  col: WwColumn
  filtro?: FiltroCol
  rows: any[]
  onAplicar: (f: FiltroCol) => void
  onLimpiar: () => void
  onVolver: () => void
}

const OPCIONES_FILAS = [5, 10, 20, 50]
const FONDO_FIJO = 'var(--cui-card-bg, #ffffff)'
// Reserva del max-height del scroll interno de la grilla (px): deja visible la
// paginación + footer + márgenes sin scroll extra de página (8rem ≈ 128px).
// UI del Data Grid — componente común REUTILIZABLE del proyecto (diseño
// [RGM] aprobado por Rafael; infraestructura de presentación, no réplica de un
// proc GX). El comportamiento de las ACCIONES de fila sí replica el GX
// (zonaww.cs:4513 GridActions / clienteww.cs:2596) con citas en cada uso.
const RESERVA_GRID = 128

// Ancho mínimo por tipo de dato: filas de UNA línea (nowrap) y el sobrante se
// resuelve con scroll horizontal (máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99" — aticket.cs:222 —
// y contrato numérico: cédula SIN separadores, regla del proyecto).
const MIN_ANCHO_POR_TIPO: Record<string, number> = {
  id: 110,
  number: 130,
  currency: 130,
  date: 130,
  bool: 100,
  text: 160,
}

// Ancho de columna: width explícito (WwColumn.width) o min-width por tipo
function estiloAnchoCol(c: WwColumn): React.CSSProperties {
  if (c.width !== undefined) return { width: c.width, minWidth: c.width }
  return { minWidth: c.minWidth ?? MIN_ANCHO_POR_TIPO[c.type ?? 'text'] }
}

function formatear(valor: any, type?: string): string {
  if (valor === null || valor === undefined || valor === '') return ''
  switch (type) {
    case 'id':
      // Identificador numérico (cédula, nro): SIN separadores (94469600) —
      // decisión de Rafael 2026-08-24 (regla AGENTS.md: cédula numérica).
      return String(valor)
    case 'number':
      // Formato universal en-US (miles con coma, sin decimales)
      return Number(valor).toLocaleString('en-US')
    case 'currency':
      // Máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99" con el SÍMBOLO configurado por empresa
      // (EmpresaTipoMoneda vía /auth/me — utils/moneda.ts). Un solo punto de
      // verdad para TODAS las columnas currency de las grillas. [RGM] 2026-10-02
      return fmtMoneda(Number(valor))
    case 'date':
      return String(valor).slice(0, 10)
    case 'bool':
      return valor ? 'Sí' : 'No'
    default:
      return String(valor)
  }
}

// Valor PLANO para el .xlsx del export genérico: el MISMO texto visible de la
// grilla (pedido Rafael). Precedencia: exportValue de la columna (display≠crudo)
// → type 'bool' (Sí/No, espejo exacto de formatear) → crudo (comportamiento
// anterior). [RGM] 2026-10-03.
function valorExport(col: WwColumn, row: any): string | number | null {
  if (col.exportValue) return col.exportValue(row)
  if (col.type === 'bool') {
    const raw = row[col.key]
    return raw === null || raw === undefined || raw === '' ? null : raw ? 'Sí' : 'No'
  }
  return row[col.key] ?? null
}

function EnlaceCelda({ valor, onClicar }: { valor: string; onClicar: () => void }) {
  return (
    <a
      href="#"
      role="button"
      className="text-primary text-decoration-none fw-medium"
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        e.preventDefault()
        // El clic en el link NO debe propagar al onRowClick de la fila
        // (evita navegar dos veces al mismo detalle).
        e.stopPropagation()
        onClicar()
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.textDecoration = 'underline'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.textDecoration = 'none'
      }}
    >
      {valor || '—'}
    </a>
  )
}

function etiquetaFiltro(col: WwColumn, f: FiltroCol): string {
  if (f.tipo === 'texto') {
    return `${col.title}: ${f.valores.map((v) => (v === '' ? t('(Vacío)') : v)).join(', ')}`
  }
  if (f.tipo === 'bool') {
    return `${col.title}: ${f.valor ? 'Sí' : 'No'}`
  }
  const partes = [f.desde !== '' && `${t('Desde')} ${f.desde}`, f.hasta !== '' && `${t('Hasta')} ${f.hasta}`].filter(
    (x): x is string => Boolean(x),
  )
  return `${col.title}: ${partes.join(' - ')}`
}

function FiltroTexto({ col, filtro, rows, onAplicar, onLimpiar, onVolver }: FiltroPanelProps) {
  const [busquedaInput, setBusquedaInput] = useState('')
  const [busquedaAplicada, setBusquedaAplicada] = useState('')
  const seleccion = filtro?.tipo === 'texto' ? filtro.valores : []
  const valores = useMemo(() => {
    const mapa = new Map<string, number>()
    for (const row of rows) {
      const v = String(row[col.key] ?? '')
      mapa.set(v, (mapa.get(v) ?? 0) + 1)
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es', { numeric: true }))
  }, [rows, col.key])
  const q = busquedaAplicada.trim().toLowerCase()
  const visibles = q ? valores.filter(([v]) => v.toLowerCase().includes(q)) : valores
  const alternar = (valor: string) => {
    const actual = new Set(seleccion)
    if (actual.has(valor)) {
      actual.delete(valor)
    } else {
      actual.add(valor)
    }
    onAplicar({ tipo: 'texto', valores: [...actual] })
  }
  return (
    <div className="px-3 py-2" style={{ width: '17rem' }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <CTooltip content={t('Volver')} placement="top">
          <CButton color="outline-secondary" size="sm" className="p-1" onClick={onVolver}>
            <CIcon icon={icons.cilArrowLeft} size="sm" />
          </CButton>
        </CTooltip>
        <strong className="text-truncate">{col.title}</strong>
      </div>
      <div className="d-flex gap-2 mb-2">
        <CFormInput
          size="sm"
          placeholder={t('Buscar')}
          value={busquedaInput}
          onChange={(e) => {
            setBusquedaInput(e.target.value)
            if (e.target.value === '') setBusquedaAplicada('')
          }}
        />
        <CButton size="sm" color="primary" onClick={() => setBusquedaAplicada(busquedaInput)}>
          {t('Buscar')}
        </CButton>
      </div>
      <div style={{ maxHeight: '12rem', overflowY: 'auto' }} className="mb-2">
        {visibles.length === 0 && (
          <div className="text-body-secondary small px-1 py-2">{t('- No se encontraron resultados -')}</div>
        )}
        {visibles.map(([valor, n]) => {
          const activo = seleccion.includes(valor)
          return (
            <div
              key={valor}
              role="button"
              onClick={() => alternar(valor)}
              className={`d-flex align-items-center gap-2 px-1 py-1 rounded ${activo ? 'bg-primary-subtle' : ''}`}
              style={{ cursor: 'pointer' }}
            >
              <CIcon
                icon={activo ? icons.cilCheckCircle : icons.cilCircle}
                size="sm"
                className={activo ? 'text-primary' : 'text-body-secondary'}
              />
              <span className="flex-grow-1 text-truncate small">{valor === '' ? t('(Vacío)') : valor}</span>
              <small className="text-body-secondary">{n}</small>
            </div>
          )
        })}
      </div>
      <CButton
        size="sm"
        color="outline-secondary"
        className="w-100"
        onClick={() => {
          setBusquedaInput('')
          setBusquedaAplicada('')
          onLimpiar()
        }}
      >
        {t('Limpiar búsqueda')}
      </CButton>
    </div>
  )
}

function FiltroRango({ col, filtro, onAplicar, onLimpiar, onVolver }: FiltroPanelProps) {
  const inicial = filtro?.tipo === 'rango' ? filtro : undefined
  const [desde, setDesde] = useState(inicial?.desde ?? '')
  const [hasta, setHasta] = useState(inicial?.hasta ?? '')
  const tipoInput = col.type === 'date' ? 'date' : 'number'
  return (
    <div className="px-3 py-2" style={{ width: '16rem' }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <CTooltip content={t('Volver')} placement="top">
          <CButton color="outline-secondary" size="sm" className="p-1" onClick={onVolver}>
            <CIcon icon={icons.cilArrowLeft} size="sm" />
          </CButton>
        </CTooltip>
        <strong className="text-truncate">{col.title}</strong>
      </div>
      <CFormLabel className="small">{t('Desde')}</CFormLabel>
      <CFormInput size="sm" type={tipoInput} value={desde} onChange={(e) => setDesde(e.target.value)} className="mb-2" />
      <CFormLabel className="small">{t('Hasta')}</CFormLabel>
      <CFormInput size="sm" type={tipoInput} value={hasta} onChange={(e) => setHasta(e.target.value)} className="mb-2" />
      <div className="d-flex gap-2">
        <CButton size="sm" color="primary" onClick={() => onAplicar({ tipo: 'rango', desde, hasta })}>
          {t('Aplicar')}
        </CButton>
        <CButton
          size="sm"
          color="outline-secondary"
          onClick={() => {
            setDesde('')
            setHasta('')
            onLimpiar()
          }}
        >
          {t('Limpiar búsqueda')}
        </CButton>
      </div>
    </div>
  )
}

function FiltroBool({ col, filtro, onAplicar }: FiltroPanelProps) {
  const actual = filtro?.tipo === 'bool' ? filtro.valor : undefined
  return (
    <div className="px-3 py-2" style={{ width: '13rem' }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <strong className="text-truncate">{col.title}</strong>
      </div>
      <CFormCheck label="Sí" checked={actual === true} onChange={() => onAplicar({ tipo: 'bool', valor: true })} />
      <CFormCheck label="No" checked={actual === false} onChange={() => onAplicar({ tipo: 'bool', valor: false })} />
    </div>
  )
}

function FiltroPanel({ col, filtro, rows, onAplicar, onLimpiar, onVolver }: FiltroPanelProps) {
  if (col.type === 'bool') {
    return <FiltroBool col={col} filtro={filtro} rows={rows} onAplicar={onAplicar} onLimpiar={onLimpiar} onVolver={onVolver} />
  }
  if (col.type === 'number' || col.type === 'currency' || col.type === 'date') {
    return <FiltroRango col={col} filtro={filtro} rows={rows} onAplicar={onAplicar} onLimpiar={onLimpiar} onVolver={onVolver} />
  }
  return <FiltroTexto col={col} filtro={filtro} rows={rows} onAplicar={onAplicar} onLimpiar={onLimpiar} onVolver={onVolver} />
}

const WwGrid: React.FC<WwGridProps> = ({
  title, columns, rows, loading = false, sinHerramientas = false, tituloCabecera = false,
  onAgregar, onExportar, onEditar, onEliminar, rowActions = [], onRowLink, onRowClick, onTotalChange, accionesMinWidth = 110, filters,
  busquedaPlaceholder = t('Filtro principal'),
}) => {
  const [filtro, setFiltro] = useState('')
  // [RGM] 2026-10-10 (pedido Rafael): personalización guardada (localStorage)
  // — se lee UNA vez al montar; cada estado arranca de ahí (podando columnas
  // que ya no existen y completando las nuevas con su default).
  const [personalizacionInicial] = useState(() => leerGridPersonalizacion(title))
  const [visible, setVisible] = useState<Record<string, boolean>>(() => {
    const g = personalizacionInicial?.visibles
    return Object.fromEntries(columns.map((c) => [c.key, g?.[c.key] ?? true]))
  })
  const [ordenColumnas, setOrdenColumnas] = useState<string[]>(() => {
    const base = columns.map((c) => c.key)
    const g = personalizacionInicial?.orden
    if (!g) return base
    const vigentes = g.filter((k) => base.includes(k))
    return [...vigentes, ...base.filter((k) => !vigentes.includes(k))]
  })
  // [RGM] 2026-09-29 (pedido Rafael): las columnas marcadas sticky arrancan
  // fijadas (ej. Acciones a la derecha en la grilla de Empresas) — salvo que
  // haya una personalización guardada.
  const [fijadasL, setFijadasL] = useState<string[]>(() => {
    const def = columns.filter((c) => c.sticky === 'left').map((c) => c.key)
    const g = personalizacionInicial?.fijadasL
    return g ? g.filter((k) => columns.some((c) => c.key === k)) : def
  })
  const [fijadasR, setFijadasR] = useState<string[]>(() => {
    const def = columns.filter((c) => c.sticky === 'right').map((c) => c.key)
    const g = personalizacionInicial?.fijadasR
    return g ? g.filter((k) => columns.some((c) => c.key === k)) : def
  })
  const [filtrosCol, setFiltrosCol] = useState<Record<string, FiltroCol>>({})
  const [orden, setOrden] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  const [pagina, setPagina] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [irPagina, setIrPagina] = useState('')
  const [vista, setVista] = useState<Vista>('grid')
  const [aviso, setAviso] = useState('')
  const [menuAbierto, setMenuAbierto] = useState<string | null>(null)
  const [panelFiltro, setPanelFiltro] = useState<string | null>(null)
  const [selAbierto, setSelAbierto] = useState(false)
  const [selDraft, setSelDraft] = useState<Record<string, boolean>>({})
  const [selBuscar, setSelBuscar] = useState('')
  // Borrador de fijaciones del selector (se aplica al pulsar Actualizar)
  const [selFixLDraft, setSelFixLDraft] = useState<string[]>([])
  const [selFixRDraft, setSelFixRDraft] = useState<string[]>([])
  const [dragSelCol, setDragSelCol] = useState<string | null>(null)
  const [dragCol, setDragCol] = useState<string | null>(null)

  // [RGM] 2026-10-10 (pedido Rafael): guardar la personalización con cada
  // cambio (orden/visibilidad/fijadas) — al refrescar se restaura.
  useEffect(() => {
    try {
      localStorage.setItem(
        claveGridPersonalizacion(title),
        JSON.stringify({ orden: ordenColumnas, visibles: visible, fijadasL, fijadasR }),
      )
    } catch {
      // localStorage lleno o bloqueado: la grilla sigue funcionando igual.
    }
  }, [title, ordenColumnas, visible, fijadasL, fijadasR])
  const [overCol, setOverCol] = useState<string | null>(null)
  const [anchos, setAnchos] = useState<Record<string, number>>({})
  const tablaRef = useRef<HTMLTableElement | null>(null)

  // Scroll INTERNO de la grilla (mejora de UI del Data Grid, NO réplica GX):
  // el wrapper .table-responsive es el scroll container (x e y) y el thead se
  // ancla a su top. El max-height se calcula del viewport y la POSICIÓN REAL
  // del wrapper (getBoundingClientRect): la tabla ocupa el espacio visible
  // debajo de ella menos la reserva, así la página no se desplaza para ver
  // registros ni paginación. El formato de moneda de las celdas SÍ es réplica:
  // máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99" (aticket.cs:222) → '$1,234,567.89'.
  const ajustarMaxAltura = useCallback(() => {
    const tabla = tablaRef.current
    if (!tabla || vista !== 'grid') return
    const wrapper = tabla.parentElement as HTMLElement | null
    if (!wrapper) return
    const natural = wrapper.scrollHeight
    const max = Math.max(140, Math.floor(window.innerHeight - wrapper.getBoundingClientRect().top - RESERVA_GRID))
    // Si el contenido entra sin recortar, no se aplica max-height (sin
    // scrollbar innecesario en tablas cortas)
    const final = natural <= max ? '' : `${max}px`
    if (wrapper.style.maxHeight !== final) wrapper.style.maxHeight = final
  }, [vista])

  useEffect(() => {
    ajustarMaxAltura()
    window.addEventListener('resize', ajustarMaxAltura)
    // El alto del card-body cambia con alertas/filtros avanzados/carga de
    // filas (todo lo que está por encima del wrapper) → recalcular
    const cardBody = tablaRef.current?.parentElement?.parentElement ?? null
    const ro = cardBody ? new ResizeObserver(ajustarMaxAltura) : null
    if (ro && cardBody) ro.observe(cardBody)
    return () => {
      window.removeEventListener('resize', ajustarMaxAltura)
      ro?.disconnect()
    }
  }, [ajustarMaxAltura, loading])

  useEffect(() => {
    const keys = columns.map((c) => c.key)
    setOrdenColumnas(keys)
    setVisible(Object.fromEntries(keys.map((k) => [k, true])))
    setFijadasL((prev) => prev.filter((k) => keys.includes(k)))
    setFijadasR((prev) => prev.filter((k) => keys.includes(k)))
    setFiltrosCol({})
    setOrden(null)
    setPagina(0)
  }, [columns])

  const tipos = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c.type])) as Record<string, string | undefined>,
    [columns],
  )

  const displayCols = useMemo(
    () =>
      ordenColumnas
        .map((k) => columns.find((c) => c.key === k))
        .filter((c): c is WwColumn => Boolean(c && visible[c.key])),
    [ordenColumnas, columns, visible],
  )
  const displayKey = displayCols.map((c) => c.key).join('|')
  const pinKey = [...fijadasL, ...fijadasR].join('|')

  const filtroActivo = (key: string): boolean => {
    const f = filtrosCol[key]
    if (!f) return false
    if (f.tipo === 'texto') return f.valores.length > 0
    if (f.tipo === 'rango') return f.desde !== '' || f.hasta !== ''
    return true
  }

  const filtradas = useMemo(() => {
    let resultado = rows
    const activos = Object.entries(filtrosCol).filter(([, f]) =>
      f.tipo === 'texto' ? f.valores.length > 0 : f.tipo === 'rango' ? f.desde !== '' || f.hasta !== '' : true,
    )
    if (activos.length > 0) {
      resultado = resultado.filter((row) =>
        activos.every(([key, f]) => {
          const v = row[key]
          if (f.tipo === 'texto') return f.valores.includes(String(v ?? ''))
          if (f.tipo === 'bool') return Boolean(v) === f.valor
          if (tipos[key] === 'date') {
            const n = String(v ?? '').slice(0, 10)
            if (f.desde !== '' && n < f.desde) return false
            if (f.hasta !== '' && n > f.hasta) return false
          } else {
            const n = v === null || v === undefined || v === '' ? NaN : Number(v)
            if (f.desde !== '' && n < Number(f.desde)) return false
            if (f.hasta !== '' && n > Number(f.hasta)) return false
          }
          return true
        }),
      )
    }
    const q = filtro.trim().toLowerCase()
    if (q) {
      resultado = resultado.filter((row) => displayCols.some((c) => String(row[c.key] ?? '').toLowerCase().includes(q)))
    }
    return resultado
  }, [rows, filtro, filtrosCol, tipos, displayKey])

  const ordenadas = useMemo(() => {
    if (!orden) return filtradas
    const mult = orden.dir === 'asc' ? 1 : -1
    return [...filtradas].sort((a, b) => {
      const va = a[orden.key]
      const vb = b[orden.key]
      if (tipos[orden.key] === 'number' || tipos[orden.key] === 'currency') {
        return (Number(va ?? 0) - Number(vb ?? 0)) * mult
      }
      return String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true }) * mult
    })
  }, [filtradas, orden, tipos])

  const total = ordenadas.length
  // Notifica el total filtrado+ordenado (sin paginar) para el contador del
  // subtítulo de página; se llama al montar y en cada cambio de filtros,
  // búsqueda, orden o rows (la página inicializa su estado con rows.length,
  // así que el primer call coincide)
  useEffect(() => {
    onTotalChange?.(total)
  }, [total, onTotalChange])
  const paginas = Math.max(1, Math.ceil(total / rowsPerPage))
  useEffect(() => {
    if (pagina >= paginas) setPagina(0)
  }, [pagina, paginas])

  const visibles = ordenadas.slice(pagina * rowsPerPage, (pagina + 1) * rowsPerPage)
  const hayAcciones = Boolean(onEditar || onEliminar || rowActions.length)

  // Fuente única de acciones (§25): onEditar/onEliminar + rowActions se
  // normalizan SIN duplicados; placements inline-start / inline-end / overflow.
  const accionesFila = (row: any) => {
    const norm = normalizarAcciones(onEditar, onEliminar, rowActions)
    return accionesParaFila(norm, row)
  }
  const accionesFin = (row: any) => accionesInlineEnd(accionesFila(row))
  const accionesMenu = (row: any) => accionesOverflow(accionesFila(row))

  const paginasLista = useMemo(() => {
    if (paginas <= 5) return Array.from({ length: paginas }, (_, i) => i + 1)
    const cur = pagina + 1
    const inicio = Math.max(2, cur - 2)
    const fin = Math.min(paginas - 1, cur + 2)
    const lista: (number | '…')[] = [1]
    if (inicio > 2) lista.push('…')
    for (let i = inicio; i <= fin; i++) lista.push(i)
    if (fin < paginas - 1) lista.push('…')
    lista.push(paginas)
    return lista
  }, [pagina, paginas])

  const tags = useMemo(
    () => [
      // La búsqueda general también es un filtro (chip "Filtro principal (valor)")
      ...(filtro.trim() ? [{ key: '__general__', texto: `${t('Filtro principal')} (${filtro.trim()})` }] : []),
      ...Object.entries(filtrosCol)
        .filter((entry) => filtroActivo(entry[0]))
        .map(([k, f]) => ({ key: k, texto: etiquetaFiltro(columns.find((c) => c.key === k) ?? { key: k, title: k }, f) })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtrosCol, filtro, columns],
  )

  useLayoutEffect(() => {
    const tabla = tablaRef.current
    if (!tabla || vista !== 'grid' || loading) return
    const medir = () => {
      const next: Record<string, number> = {}
      tabla.querySelectorAll('th[data-col]').forEach((el) => {
        const th = el as HTMLElement
        if (th.dataset.col) next[th.dataset.col] = th.offsetWidth
      })
      setAnchos((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
    }
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(tabla)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, loading, displayKey, pinKey, JSON.stringify(visible)])

  const desfases = useMemo(() => {
    const mapa: Record<string, { left?: number; right?: number }> = {}
    let accL = 0
    for (const c of displayCols) {
      if (fijadasL.includes(c.key)) {
        mapa[c.key] = { ...mapa[c.key], left: accL }
        accL += anchos[c.key] ?? 0
      }
    }
    let accR = 0
    for (let i = displayCols.length - 1; i >= 0; i--) {
      const c = displayCols[i]
      if (fijadasR.includes(c.key)) {
        mapa[c.key] = { ...mapa[c.key], right: accR }
        accR += anchos[c.key] ?? 0
      }
    }
    return mapa
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayKey, pinKey, anchos])

  const toggleOrden = (col: WwColumn) => {
    if (col.sortable === false) return
    setOrden((prev) =>
      prev?.key === col.key
        ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: 'asc' },
    )
  }

  const grupoDe = (key: string) => (fijadasL.includes(key) ? 'L' : fijadasR.includes(key) ? 'R' : 'M')

  const reordenar = (desde: string, hasta: string) => {
    if (grupoDe(desde) !== grupoDe(hasta)) return
    setOrdenColumnas((prev) => {
      const next = [...prev]
      const from = next.indexOf(desde)
      const to = next.indexOf(hasta)
      if (from < 0 || to < 0 || from === to) return prev
      next.splice(from, 1)
      next.splice(to, 0, desde)
      return next
    })
  }

  const fijarIzquierda = (key: string) => {
    const nuevaL = fijadasL.includes(key) ? fijadasL : [...fijadasL, key]
    setFijadasL(nuevaL)
    setFijadasR((prev) => prev.filter((k) => k !== key))
    setOrdenColumnas((prev) => {
      const next = prev.filter((k) => k !== key)
      next.splice(fijadasL.length, 0, key)
      return next
    })
  }

  const fijarDerecha = (key: string) => {
    setFijadasL((prev) => prev.filter((k) => k !== key))
    setFijadasR((prev) => (prev.includes(key) ? prev : [...prev, key]))
    setOrdenColumnas((prev) => {
      const next = prev.filter((k) => k !== key)
      next.push(key)
      return next
    })
  }

  const quitarFijacion = (key: string) => {
    setFijadasL((prev) => prev.filter((k) => k !== key))
    setFijadasR((prev) => prev.filter((k) => k !== key))
    setOrdenColumnas((prev) => {
      const next = prev.filter((k) => k !== key)
      const rCount = fijadasR.includes(key) ? fijadasR.length - 1 : fijadasR.length
      next.splice(next.length - rCount, 0, key)
      return next
    })
  }

  const limpiarFiltro = (key: string) => {
    if (key === '__general__') {
      setFiltro('')
      setPagina(0)
      return
    }
    setFiltrosCol((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    setPagina(0)
  }

  // Limpia búsqueda principal + filtros de columnas (menú del embudo y botón
  // directo en la fila de chips de filtros activos)
  const limpiarTodosLosFiltros = () => {
    setFiltro('')
    setFiltrosCol({})
    setPagina(0)
  }

  const aplicarFiltro = (key: string) => (f: FiltroCol) => {
    if ((f.tipo === 'texto' && f.valores.length === 0) || (f.tipo === 'rango' && f.desde === '' && f.hasta === '')) {
      limpiarFiltro(key)
      return
    }
    setFiltrosCol((prev) => {
      const actual = prev[key]
      if (f.tipo === 'bool' && actual?.tipo === 'bool' && actual.valor === f.valor) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: f }
    })
    setPagina(0)
  }

  const abrirMenu = (key: string) => setMenuAbierto(key)

  const cerrarMenu = (key: string) => {
    if (menuAbierto === key) {
      setMenuAbierto(null)
      setPanelFiltro(null)
    }
  }

  const navegarPagina = () => {
    const n = parseInt(irPagina, 10)
    if (!isNaN(n)) setPagina(Math.min(Math.max(n - 1, 0), paginas - 1))
    setIrPagina('')
  }

  const exportar = async () => {
    // Descripción de los filtros ACTIVOS de la grilla — buscador principal y
    // filtros por columna. Viaja igual a las DOS ramas: al handler custom de
    // la página (export .xlsx de Préstamos) y al export genérico del backend,
    // para que el archivo diga cómo viene filtrado (pedido Rafael).
    const descripcion: { label: string; valor: string }[] = []
    if (filtro.trim()) descripcion.push({ label: 'Filtro', valor: filtro.trim() })
    for (const [key, f] of Object.entries(filtrosCol)) {
      const titulo = displayCols.find((c) => c.key === key)?.title ?? key
      if (f.tipo === 'texto' && f.valores.length > 0) {
        descripcion.push({ label: titulo, valor: f.valores.join(', ') })
      } else if (f.tipo === 'rango') {
        descripcion.push({ label: titulo, valor: `${f.desde || '…'} - ${f.hasta || '…'}` })
      } else if (f.tipo === 'bool') {
        descripcion.push({ label: titulo, valor: f.valor ? 'Sí' : 'No' })
      }
    }

    if (onExportar) {
      // Export custom de la página (las filas filtradas+ordenadas actuales,
      // sin paginar).
      onExportar(ordenadas, descripcion)
      return
    }

    // Export genérico: .xlsx real generado por el backend (ClosedXML) con el
    // mismo formato aprobado (filtros en fila 2, encabezado azul #0070C0,
    // tipos reales). Reemplaza al CSV armado en el navegador.
    try {
      const blob = await api.postBlob('/export/grid', {
        titulo: title,
        columnas: displayCols.map((c) => ({ titulo: c.title, tipo: c.type ?? 'text' })),
        filas: ordenadas.map((row) => displayCols.map((c) => valorExport(c, row))),
        filtros: descripcion,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${title}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setAviso(e?.message ?? t('No se pudo exportar'))
    }
  }

  /// Menú ⋮ de acciones overflow (CRUD/secundarias): SOLO acciones no visibles
/// (§11); destructivas al final, separadas y en danger (§10). Posición réplica
/// del GX (el menú del grid va al INICIO de la fila — zonaww.cs:4513:
/// vENRUTAR, vRECIBOS, cmbavGridactions, ... — GridActions antes de los datos).
function MenuAccionesOverflow({ acciones, row, iconoAccion }: {
  acciones: any[]; row: any; iconoAccion: (n?: string) => React.ReactNode
}) {
  return (
    <CDropdown
      portal
      popperConfig={{
        strategy: 'fixed',
        modifiers: [
          { name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-end', 'bottom-start', 'top-start'] } },
          { name: 'preventOverflow', options: { boundary: 'viewport' } },
        ],
      }}
    >
      <CDropdownToggle size="sm" caret={false}>
        <CIcon icon={icons.cilOptions} />
      </CDropdownToggle>
      <CDropdownMenu>
        {acciones.map((a: any, idx: number, arr: any[]) => {
          const esDestructiva = a.destructive
          const previaDestructiva = idx > 0 && arr[idx - 1].destructive
          return (
            <span key={a.id}>
              {esDestructiva && !previaDestructiva && <CDropdownDivider />}
              <CDropdownItem
                onClick={() => a.onClick(row)}
                className={esDestructiva ? 'text-danger' : undefined}
              >
                {iconoAccion(a.icon)}
                {a.label}
              </CDropdownItem>
            </span>
          )
        })}
      </CDropdownMenu>
    </CDropdown>
  )
}

const iconoAccion = (nombre?: string) =>
    nombre && (icons as Record<string, any>)[nombre] ? (
      <CIcon icon={(icons as Record<string, any>)[nombre]} className="me-1" size="sm" />
    ) : null

  const iconoEstado = (key: string) => {
    if (filtroActivo(key)) return icons.cilFilter
    if (orden?.key === key) return orden.dir === 'asc' ? icons.cilSortAscending : icons.cilSortDescending
    return icons.cilCaretBottom
  }

  const gruposSelector = useMemo(() => {
    const encaja = (c: WwColumn) => selBuscar === '' || c.title.toLowerCase().includes(selBuscar.toLowerCase())
    // Los grupos del selector usan el BORRADOR de fijaciones (se aplica al Actualizar)
    const grupos = [
      { titulo: t('Fijas a la izquierda'), grupo: 'L', cols: columns.filter((c) => selFixLDraft.includes(c.key) && encaja(c)) },
      { titulo: t('No fijas'), grupo: 'M', cols: columns.filter((c) => !selFixLDraft.includes(c.key) && !selFixRDraft.includes(c.key) && encaja(c)) },
      { titulo: t('Fijas a la derecha'), grupo: 'R', cols: columns.filter((c) => selFixRDraft.includes(c.key) && encaja(c)) },
    ]
    return grupos
  }, [columns, selFixLDraft, selFixRDraft, selBuscar])

  // Mover una columna entre grupos del selector (drag & drop, como el WWP)
  const moverColumnaSelector = (key: string, grupo: 'L' | 'M' | 'R') => {
    setSelFixLDraft((l) => (grupo === 'L' ? (l.includes(key) ? l : [...l, key]) : l.filter((k) => k !== key)))
    setSelFixRDraft((r) => (grupo === 'R' ? (r.includes(key) ? r : [...r, key]) : r.filter((k) => k !== key)))
  }

  const renderVistaTarjetas = () => {
    if (visibles.length === 0) {
      return <div className="text-center text-body-secondary py-4">{t('No se encontraron registros')}</div>
    }
    const c0 = displayCols[0]
    const resto = displayCols.slice(1, vista === 'cards-big' ? 7 : vista === 'cards-info' ? 5 : 4)
    const conEtiquetas = vista === 'cards-info' || vista === 'cards-big'
    return (
      <div
        className="mb-3"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}
      >
        {visibles.map((row, i) => {
          const titulo = c0 ? formatear(row[c0.key], c0.type) : ''
          return (
            <CCard key={i} className="shadow-sm">
              <CCardHeader className="fw-semibold text-truncate" title={titulo}>
                {c0?.link && onRowLink ? (
                  <EnlaceCelda valor={titulo} onClicar={() => onRowLink(c0.key, row)} />
                ) : (
                  titulo || '—'
                )}
              </CCardHeader>
              <CCardBody>
                {conEtiquetas ? (
                  resto.map((c) => (
                    <div key={c.key} className="mb-1">
                      <small className="text-body-secondary">{c.title}</small>
                      <div className="text-truncate" title={formatear(row[c.key], c.type)}>
                        {formatear(row[c.key], c.type) || '—'}
                      </div>
                    </div>
                  ))
                ) : (
                  <small className="text-body-secondary">
                    {resto.map((c) => formatear(row[c.key], c.type)).filter((v) => v !== '').map((v, j) => (
                      <span key={j} className="d-block text-truncate" title={v}>
                        {v}
                      </span>
                    ))}
                  </small>
                )}
              </CCardBody>
            </CCard>
          )
        })}
      </div>
    )
  }

  return (
    <>
    {/* [RGM] 2026-09-17 (rediseño Rafael — "mejores prácticas modernas"): el
        toolbar es una TARJETA PROPIA arriba de la grilla (no unida a la
        tabla): fila de acciones (Agregar + utilidades), luego la búsqueda y
        debajo los filtros; mismo borde redondo que la tarjeta de la grilla. */}
    {!sinHerramientas && (
    <div className="ww-toolbar-card">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {onAgregar && (
            <CButton color="primary" size="sm" onClick={onAgregar}>
              <CIcon icon={icons.cilPlus} className="me-1" size="sm" />
              {t('Agregar')}
            </CButton>
          )}
          <CButton color="outline-secondary" size="sm" onClick={exportar}>
            <CIcon icon={icons.cilCloudDownload} className="me-1" size="sm" />
            {t('Exportar')}
          </CButton>
          {/* Selecciona columnas — configuración de visualización */}
          <CDropdown
            visible={selAbierto}
            autoClose="outside"
            onShow={() => {
              setSelDraft({ ...visible })
              setSelFixLDraft([...fijadasL])
              setSelFixRDraft([...fijadasR])
              setSelBuscar('')
              setSelAbierto(true)
            }}
            onHide={() => setSelAbierto(false)}
          >
            <CDropdownToggle color="outline-secondary" size="sm">
              <CIcon icon={icons.cilColumns} className="me-1" size="sm" />
              {t('Selecciona columnas')}
            </CDropdownToggle>
            <CDropdownMenu style={{ width: '18rem', maxHeight: '26rem', overflowY: 'auto' }}>
              <div className="px-3 py-2">
                <CFormInput
                  size="sm"
                  placeholder={t('Buscar columna')}
                  value={selBuscar}
                  onChange={(e) => setSelBuscar(e.target.value)}
                />
              </div>
              {gruposSelector.map((g) => (
                <div
                  key={g.titulo}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragSelCol) moverColumnaSelector(dragSelCol, g.grupo as 'L' | 'M' | 'R')
                    setDragSelCol(null)
                  }}
                  style={{ minHeight: '2rem' }}
                >
                  <div className="dropdown-header">{g.titulo}</div>
                  {g.cols.length === 0 && (
                    <div className="px-3 py-1 text-body-secondary small">{t('(Ninguna)')}</div>
                  )}
                  {g.cols.map((c) => (
                    <div
                      key={c.key}
                      draggable
                      onDragStart={() => setDragSelCol(c.key)}
                      onDragEnd={() => setDragSelCol(null)}
                      className="px-3 py-1"
                      style={{ cursor: 'grab' }}
                    >
                      <CFormCheck
                        label={c.title}
                        checked={selDraft[c.key] ?? true}
                        onChange={(e) => setSelDraft({ ...selDraft, [c.key]: e.target.checked })}
                      />
                    </div>
                  ))}
                </div>
              ))}
              <CDropdownDivider />
              <div className="px-3 py-2 d-flex flex-column gap-2">
                <CButton
                  size="sm"
                  color="outline-secondary"
                  onClick={() => {
                    setSelDraft(Object.fromEntries(columns.map((c) => [c.key, true])))
                    setSelFixLDraft([])
                    setSelFixRDraft([])
                  }}
                >
                  {t('Restaurar la configuración predeterminada')}
                </CButton>
                <CButton
                  size="sm"
                  color="primary"
                  onClick={() => {
                    setVisible(selDraft)
                    setFijadasL(selFixLDraft)
                    setFijadasR(selFixRDraft)
                    setPagina(0)
                    setSelAbierto(false)
                  }}
                >
                  {t('Actualizar')}
                </CButton>
              </div>
            </CDropdownMenu>
          </CDropdown>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <CButtonGroup size="sm" className="me-1">
            <CTooltip content={t('Grilla')} placement="top">
              <CButton color={vista === 'grid' ? 'primary' : 'outline-secondary'} onClick={() => setVista('grid')}>
                <CIcon icon={icons.cilGrid} size="sm" />
              </CButton>
            </CTooltip>
            <CTooltip content={t('Tarjetas pequeñas')} placement="top">
              <CButton color={vista === 'cards-small' ? 'primary' : 'outline-secondary'} onClick={() => setVista('cards-small')}>
                <CIcon icon={icons.cilApplications} size="sm" />
              </CButton>
            </CTooltip>
            <CTooltip content={t('Tarjetas con info')} placement="top">
              <CButton color={vista === 'cards-info' ? 'primary' : 'outline-secondary'} onClick={() => setVista('cards-info')}>
                <CIcon icon={icons.cilInfo} size="sm" />
              </CButton>
            </CTooltip>
            <CTooltip content={t('Tarjetas grandes')} placement="top">
              <CButton color={vista === 'cards-big' ? 'primary' : 'outline-secondary'} onClick={() => setVista('cards-big')}>
                <CIcon icon={icons.cilBorderAll} size="sm" />
              </CButton>
            </CTooltip>
          </CButtonGroup>
          {/* Embudo de filtros — junto al buscador */}
          <CDropdown>
            <CTooltip content={t('Filtros')} placement="top">
              <CDropdownToggle color="outline-secondary" size="sm">
                <CIcon icon={icons.cilFilter} size="sm" />
                {tags.length > 0 && <CBadge color="primary" className="ms-1">{tags.length}</CBadge>}
              </CDropdownToggle>
            </CTooltip>
            <CDropdownMenu>
              <CDropdownItem onClick={limpiarTodosLosFiltros}>
                {t('Limpiar filtros')}
              </CDropdownItem>
              <CDropdownItem onClick={() => setAviso(t('Guardar filtro como... — pendiente de réplica'))}>
                {t('Guardar filtro como...')}
              </CDropdownItem>
            </CDropdownMenu>
          </CDropdown>
          {/* [RGM] 2026-09-17 (reporte Rafael): la búsqueda vuelve a su lugar
              ORIGINAL — al FINAL de la fila de botones, alineada a la derecha. */}
          <div className="position-relative">
            <CIcon
              icon={icons.cilSearch}
              size="sm"
              className="position-absolute top-50 translate-middle-y ms-3 text-body-secondary"
            />
            <CFormInput
              size="sm"
              style={{ width: '18rem', minWidth: '12rem', paddingLeft: '2.25rem' }}
              placeholder={busquedaPlaceholder}
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value)
                setPagina(0)
              }}
            />
          </div>
        </div>
      </div>
      {/* [RGM] 2026-09-17 (pedido Rafael): los filtros de la grilla van
          DEBAJO de la fila de botones/búsqueda. */}
      {filters && <div className="mt-2">{filters}</div>}
      {/* Filtros activos — línea SOLO cuando existen (sin franja vacía) */}
      {tags.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-2 px-3 pb-2">
          {tags.map((tag) => (
            <CBadge key={tag.key} color="primary" className="d-inline-flex align-items-center gap-1 fw-normal">
              {tag.texto}
              <CTooltip content={t('Quitar filtro')} placement="top">
                <CIcon
                  icon={icons.cilX}
                  size="sm"
                  role="button"
                  onClick={() => limpiarFiltro(tag.key)}
                  style={{ cursor: 'pointer' }}
                />
              </CTooltip>
            </CBadge>
          ))}
          <CButton size="sm" color="link" className="p-0" onClick={limpiarTodosLosFiltros}>
            {t('Limpiar filtros')}
          </CButton>
        </div>
      )}
    </div>
    )}
    <CCard className="mb-4">
      {/* [RGM] 2026-09-29 (pedido Rafael): título en la CABECERA de la grilla
          (como las pantallas de editar) — solo con tituloCabecera activo. */}
      {tituloCabecera && title && (
        <CCardHeader className="fw-semibold py-2">{title}</CCardHeader>
      )}
      <CCardBody>

        {aviso && (
          <CAlert color="warning" dismissible className="mb-3 py-2" onClose={() => setAviso('')}>
            {aviso}
          </CAlert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <CSpinner color="primary" />
          </div>
        ) : vista === 'grid' ? (
          <CTable ref={tablaRef} hover responsive striped align="middle" className="mb-3 ww-grid-table">
            <CTableHead>
              <CTableRow>
                {displayCols.map((c) => {
                  const f = desfases[c.key]
                  const fijada = fijadasL.includes(c.key) || fijadasR.includes(c.key)
                  return (
                    <CTableHeaderCell
                      key={c.key}
                      data-col={c.key}
                      draggable
                      onDragStart={(e) => {
                        setDragCol(c.key)
                        e.dataTransfer.effectAllowed = 'move'
                        e.dataTransfer.setData('text/plain', c.key)
                      }}
                      onDragOver={(e) => {
                        if (dragCol && dragCol !== c.key) {
                          e.preventDefault()
                          setOverCol(c.key)
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault()
                        if (dragCol) reordenar(dragCol, c.key)
                        setDragCol(null)
                        setOverCol(null)
                      }}
                      onDragEnd={() => {
                        setDragCol(null)
                        setOverCol(null)
                      }}
                      onClick={(e) => {
                        const t = e.target as HTMLElement
                        if (panelFiltro === c.key || t.closest('button, a, input, label, .dropdown-menu')) return
                        toggleOrden(c)
                      }}
                      style={{
                        cursor: c.sortable === false ? undefined : 'pointer',
                        // Sticky de fijación: posición/offsets inline; el sticky vertical
                        // (top) y el z-index se resuelven en style.scss (.ww-grid-table)
                        ...(f ? { position: 'sticky' as const, left: f.left, right: f.right } : {}),
                        ...estiloAnchoCol(c),
                        ...(overCol === c.key && dragCol && dragCol !== c.key
                          ? { boxShadow: 'inset 0 -3px 0 0 var(--cui-primary)' }
                          : {}),
                      }}
                      className={f ? (f.left !== undefined ? 'fixed-left' : 'fixed-right') : undefined}
                    >
                      <span className={`d-flex align-items-center gap-1 ${fijada ? 'text-primary' : undefined}`}>
                        <span className="text-truncate">{c.title}</span>
                        <CDropdown
                          visible={menuAbierto === c.key}
                          autoClose="outside"
                          onShow={() => abrirMenu(c.key)}
                          onHide={() => cerrarMenu(c.key)}
                          className="d-inline-flex"
                          // Portal + popper (igual que el menú de Acciones): el menú
                          // de columna escapa el contenedor scrollable y se
                          // auto-posiciona sin quedar detrás del sticky.
                          portal
                          popperConfig={{
                            strategy: 'fixed',
                            modifiers: [
                              { name: 'flip', options: { fallbackPlacements: ['bottom-start', 'top-start', 'bottom-end', 'top-end'] } },
                              { name: 'preventOverflow', options: { boundary: 'viewport' } },
                            ],
                          }}
                        >
                          <CTooltip content={t('Opciones')} placement="top">
                            <CDropdownToggle size="sm" caret={false} className="p-1 border-0">
                              <CIcon icon={iconoEstado(c.key)} size="sm" className={filtroActivo(c.key) ? 'text-primary' : undefined} />
                            </CDropdownToggle>
                          </CTooltip>
                        <CDropdownMenu style={{ minWidth: panelFiltro === c.key ? '17rem' : '13rem' }}>
                          {panelFiltro === c.key ? (
                            <FiltroPanel
                              col={c}
                              filtro={filtrosCol[c.key]}
                              rows={rows}
                              onAplicar={aplicarFiltro(c.key)}
                              onLimpiar={() => limpiarFiltro(c.key)}
                              onVolver={() => setPanelFiltro(null)}
                            />
                          ) : (
                            <>
                              <CDropdownItem
                                active={orden?.key === c.key && orden.dir === 'asc'}
                                onClick={() => {
                                  setOrden({ key: c.key, dir: 'asc' })
                                  cerrarMenu(c.key)
                                }}
                              >
                                <CIcon icon={icons.cilSortAscending} className="me-1" size="sm" />
                                {t('Ordenar de A a Z')}
                              </CDropdownItem>
                              <CDropdownItem
                                active={orden?.key === c.key && orden.dir === 'desc'}
                                onClick={() => {
                                  setOrden({ key: c.key, dir: 'desc' })
                                  cerrarMenu(c.key)
                                }}
                              >
                                <CIcon icon={icons.cilSortDescending} className="me-1" size="sm" />
                                {t('Ordenar de Z a A')}
                              </CDropdownItem>
                              <CDropdownDivider />
                              {fijada ? (
                                <CDropdownItem
                                  onClick={() => {
                                    quitarFijacion(c.key)
                                    cerrarMenu(c.key)
                                  }}
                                >
                                  {t('Quitar fijación')}
                                </CDropdownItem>
                              ) : (
                                <>
                                  <CDropdownItem
                                    onClick={() => {
                                      fijarIzquierda(c.key)
                                      cerrarMenu(c.key)
                                    }}
                                  >
                                    {t('Fijar a la izquierda')}
                                  </CDropdownItem>
                                  <CDropdownItem
                                    onClick={() => {
                                      fijarDerecha(c.key)
                                      cerrarMenu(c.key)
                                    }}
                                  >
                                    {t('Fijar a la derecha')}
                                  </CDropdownItem>
                                </>
                              )}
                              <CDropdownDivider />
                              <CDropdownItem onClick={() => setPanelFiltro(c.key)}>
                                <CIcon icon={icons.cilFilter} className="me-1" size="sm" />
                                {t('Filtros')}
                              </CDropdownItem>
                            </>
                          )}
                        </CDropdownMenu>
                      </CDropdown>
                      </span>
                    </CTableHeaderCell>
                  )
                })}
                {hayAcciones && (
                  <CTableHeaderCell className="text-end ww-acciones-th" style={{ minWidth: accionesMinWidth }}>
                    {t('Acciones')}
                  </CTableHeaderCell>
                )}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {visibles.map((row, i) => (
                <CTableRow
                  key={i}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {displayCols.map((c) => {
                    const f = desfases[c.key]
                    return (
                      <CTableDataCell
                        key={c.key}
                        className={f ? (f.left !== undefined ? 'fixed-left' : 'fixed-right') : undefined}
                        style={{
                          ...estiloAnchoCol(c),
                          ...(f ? { position: 'sticky' as const, left: f.left, right: f.right, backgroundColor: FONDO_FIJO } : {}),
                        }}
                      >
                        {c.render ? (
                          c.render(row)
                        ) : c.link && onRowLink ? (
                          <EnlaceCelda valor={formatear(row[c.key], c.type)} onClicar={() => onRowLink(c.key, row)} />
                        ) : (
                          formatear(row[c.key], c.type)
                        )}
                      </CTableDataCell>
                    )
                  })}
                  {hayAcciones && (
                    <CTableDataCell
                      className="text-end ww-acciones-td"
                      style={{ minWidth: accionesMinWidth }}
                      // El clic en acciones NO debe disparar onRowClick de la fila
                      // (event bubbling abría el detalle al usar el menú).
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Iconos de acciones operativas (inline-end — ej. 📞 Contactar,
                          📄 Consolidado en clientes): SOLO icono + tooltip, dentro de
                          la columna de acciones (no dañan las columnas). Caben
                          2 iconos + ⋮ en el min-width de 110px sin ampliarlo. */}
                      {accionesFin(row).map((a) => (
                        <CTooltip key={a.id} content={a.label}>
                          <CButton
                            size="sm"
                            color="link"
                            className={`p-1 ww-accion-visible${a.destructive ? ' text-danger' : ''}`}
                            aria-label={a.label}
                            onClick={(e) => { e.stopPropagation(); a.onClick(row) }}
                          >
                            {iconoAccion(a.icon)}
                          </CButton>
                        </CTooltip>
                      ))}
                      {accionesMenu(row).length > 0 && (
                        <MenuAccionesOverflow acciones={accionesMenu(row)} row={row} iconoAccion={iconoAccion} />
                      )}
                    </CTableDataCell>
                  )}
                </CTableRow>
              ))}
              {visibles.length === 0 && (
                <CTableRow>
                  <CTableDataCell
                    colSpan={displayCols.length + (hayAcciones ? 1 : 0)}
                    className="text-center text-body-secondary"
                  >
                    {t('No se encontraron registros')}
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
        ) : (
          renderVistaTarjetas()
        )}

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          {/* IZQUIERDA: Página # de # (menú desplegado hacia ARRIBA — dropup) */}
          <CDropdown placement="top-start">
            <CDropdownToggle color="outline-secondary" size="sm">
              {t('Página')} {pagina + 1} {t('de')} {paginas}
            </CDropdownToggle>
            <CDropdownMenu>
              <div className="px-3 py-1 small text-body-secondary">{t('Registros por página:')}</div>
              {OPCIONES_FILAS.map((n) => (
                <CDropdownItem
                  key={n}
                  active={rowsPerPage === n}
                  onClick={() => {
                    setRowsPerPage(n)
                    setPagina(0)
                  }}
                >
                  {n} {t('filas')}
                </CDropdownItem>
              ))}
              <CDropdownDivider />
              <div className="px-3 py-1 small text-body-secondary">{t('Ir a página:')}</div>
              <div className="px-3 d-flex gap-1">
                <CFormInput
                  type="number"
                  size="sm"
                  value={irPagina}
                  min={1}
                  max={paginas}
                  style={{ width: '5rem' }}
                  onChange={(e) => setIrPagina(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navegarPagina()
                  }}
                />
                <CButton size="sm" color="primary" onClick={navegarPagina}>{t('Ir')}</CButton>
              </div>
            </CDropdownMenu>
          </CDropdown>
          {/* CENTRO: filtros activos */}
          {tags.length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2">
              <small className="text-body-secondary">{t('Filtrando por:')}</small>
              {tags.map((tag) => (
                <CBadge key={tag.key} color="primary" className="d-inline-flex align-items-center gap-1 fw-normal">
                  {tag.texto}
                <CTooltip content={t('Quitar filtro')} placement="top">
                  <CIcon
                    icon={icons.cilX}
                    size="sm"
                    role="button"
                    onClick={() => limpiarFiltro(tag.key)}
                    style={{ cursor: 'pointer' }}
                  />
                </CTooltip>
                </CBadge>
              ))}
            </div>
          )}
          {/* DERECHA: Ant · páginas · Sig */}
          <div className="d-flex flex-wrap align-items-center gap-1">
            <CTooltip content={t('Anterior')} placement="top">
              <CButton
                color="outline-secondary"
                size="sm"
                disabled={pagina === 0}
                aria-label={t('Anterior')}
                onClick={() => setPagina((p) => Math.max(0, p - 1))}
              >
                <CIcon icon={icons.cilChevronLeft} size="sm" />
              </CButton>
            </CTooltip>
            <div className="d-flex flex-wrap align-items-center gap-1">
              {paginasLista.map((p, i) =>
                p === '…' ? (
                  <span key={`ellipsis-f-${i}`} className="text-body-secondary px-1">…</span>
                ) : (
                  <CButton
                    key={p}
                    color={p === pagina + 1 ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setPagina(p - 1)}
                  >
                    {p}
                  </CButton>
                ),
              )}
            </div>
            <CTooltip content={t('Siguiente')} placement="top">
              <CButton
                color="outline-secondary"
                size="sm"
                disabled={pagina >= paginas - 1}
                aria-label={t('Siguiente')}
                onClick={() => setPagina((p) => Math.min(paginas - 1, p + 1))}
              >
                <CIcon icon={icons.cilChevronRight} size="sm" />
              </CButton>
            </CTooltip>
          </div>
        </div>
      </CCardBody>
    </CCard>
    </>
  )
}

export default WwGrid
